import { useEffect } from "react";
import api from "../services/api";

// Emits schema.org Product data for the page being viewed. This is what lets
// Google show the price, "in stock" and star ratings inside the search result
// itself rather than just a blue link — the single biggest CTR lever available
// at a given ranking position.
//
// Injected per product and removed on unmount, so navigating between products
// can never leave the previous product's price on the page.
const SITE = "https://fmartskorea.com";
const TAG_ID = "product-jsonld";

const plain = (html) => (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

export default function ProductSchema({ product, images = [] }) {
  useEffect(() => {
    if (!product?._id) return;
    let cancelled = false;

    const build = (rating) => {
      const price = product.is_on_sale && product.sale_price
        ? product.sale_price
        : (product.sell_price || product.price || 0);

      const imageUrls = images.map((i) => i.url).filter(Boolean);
      const inStock = (product.stock_quantity ?? 0) > 0;

      const data = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        description: plain(product.description).slice(0, 400) || product.name,
        url: `${SITE}/product/${product._id}`,
        ...(imageUrls.length ? { image: imageUrls } : {}),
        ...(product.SKU ? { sku: product.SKU } : {}),
        // product_code is an EAN barcode on packaged goods, but an internal
        // reference like "FM-350" on others — only the real barcode is a gtin13.
        ...(/^\d{13}$/.test(product.product_code || "") ? { gtin13: product.product_code } : {}),
        ...(product.brand ? { brand: { "@type": "Brand", name: product.brand } } : {}),
        ...(product.country_of_origin ? { countryOfOrigin: product.country_of_origin } : {}),
        offers: {
          "@type": "Offer",
          url: `${SITE}/product/${product._id}`,
          priceCurrency: "KRW",
          price: String(price),
          availability: inStock
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
          itemCondition: "https://schema.org/NewCondition",
          seller: { "@type": "Organization", name: "FMart Korea" },
        },
        // Only ever claimed when real approved reviews exist and are shown on
        // the page — Google treats invented ratings as a manual-action offence.
        ...(rating && rating.count > 0
          ? {
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: Number(rating.average).toFixed(1),
                reviewCount: rating.count,
              },
            }
          : {}),
      };

      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = TAG_ID;
      el.text = JSON.stringify(data);
      document.getElementById(TAG_ID)?.remove();
      document.head.appendChild(el);
    };

    // The rating lives behind the reviews endpoint; if it's unavailable or the
    // module is off, the schema is still emitted without it.
    api.get(`/reviews/product/${product._id}`)
      .then((res) => { if (!cancelled) build(res.data?.summary); })
      .catch(() => { if (!cancelled) build(null); });

    return () => {
      cancelled = true;
      document.getElementById(TAG_ID)?.remove();
    };
  }, [product, images]);

  return null;
}
